<script setup>
  import { computed } from 'vue';
  import { ArrowLeftIcon, CalendarIcon, ClockIcon } from '@heroicons/vue/solid';
  import { formatDate, formatTime } from '@/utils/date';
  import { isNotSoldOut, saleOpen } from '@/composables/useEvent';
  import TicketsAvailableBanner from '@/components/events/TicketsAvailableBanner.vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const props = defineProps({
    project: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  });

  // Get events from project object
  const events = computed(() => {
    return props.project?.events || [];
  });

  const emit = defineEmits(['back', 'select-event']);

  // Get project square image (not banner)
  const projectImage = computed(() => {
    if (!props.project.assets) return null;
    return props.project.assets.find((a) => a.type === 'square');
  });

  // Check if project has no image - show gradient
  const hasNoImage = computed(() => !projectImage.value);

  // Helper function to generate event count text
  const eventCountText = computed(() => {
    const ticketEvents = props.project.ticketEventCount || 0;
    const productEvents = props.project.productEventCount || 0;

    if (ticketEvents === 0 && productEvents === 0) {
      return '';
    }

    const parts = [];

    if (ticketEvents > 0) {
      const key =
        ticketEvents === 1
          ? 'tickets.projects.performance'
          : 'tickets.projects.performances';
      parts.push(`${ticketEvents} ${t(key)}`);
    }

    if (productEvents > 0) {
      const key =
        productEvents === 1
          ? 'tickets.projects.sale'
          : 'tickets.projects.sales';
      parts.push(`${productEvents} ${t(key)}`);
    }

    return parts.join(' en ');
  });

  function handleSelectEvent(event) {
    if (isNotSoldOut(event) && saleOpen(event)) {
      emit('select-event', event);
    }
  }

  function goBack() {
    emit('back');
  }
</script>

<template>
  <div>
    <!-- Back Button - Above title on mobile only -->
    <button
      type="button"
      class="group mb-2 inline-flex items-center text-xs font-medium text-gray-400 transition-colors md:hidden"
      @click="goBack"
    >
      <ArrowLeftIcon
        class="group-hover:text-primary-600 mr-1 h-3 w-3 transition-colors"
      />
      <span class="group-hover:text-primary-600 transition-colors">
        {{ $t('tickets.projects.back_to_projects') }}
      </span>
    </button>

    <!-- Project Header - Compact with small square image and back button -->
    <div class="mb-4 overflow-hidden rounded-lg bg-white shadow md:mb-6">
      <div class="flex items-center gap-3 p-4 md:gap-4 md:p-6">
        <!-- Small Square Image or Gradient - Smaller on mobile -->
        <div
          class="h-12 w-12 shrink-0 overflow-hidden rounded-lg md:h-16 md:w-16"
        >
          <img
            v-if="!hasNoImage"
            :src="projectImage.url"
            :alt="project.name"
            class="h-full w-full object-cover"
          />
          <!-- Gradient fallback with decorative circles -->
          <div
            v-else
            class="from-primary-50 to-primary-100 relative h-full w-full overflow-hidden rounded-lg bg-gradient-to-br"
          >
            <div
              class="bg-primary-200 absolute -right-2 -top-2 h-8 w-8 rounded-full opacity-40"
            ></div>
            <div
              class="bg-primary-300 absolute -bottom-1 -left-1 h-6 w-6 rounded-full opacity-30"
            ></div>
            <div
              class="bg-primary-200 absolute right-1 top-6 h-4 w-4 rounded-full opacity-20"
            ></div>
          </div>
        </div>

        <!-- Project Info - Smaller on mobile -->
        <div class="min-w-0 flex-1">
          <h2 class="text-lg font-bold text-gray-900 md:text-xl">
            {{ project.name }}
          </h2>
          <p
            v-if="project.info"
            class="mt-0.5 text-xs text-gray-600 md:mt-1 md:text-sm"
          >
            {{ project.info }}
          </p>
          <div class="mt-0.5 text-xs text-gray-500 md:mt-1 md:text-sm">
            {{ eventCountText }}
          </div>
        </div>

        <!-- Back Button - Right aligned on desktop, hidden on mobile -->
        <button
          type="button"
          class="hover:text-primary-600 group hidden shrink-0 items-center text-sm font-medium text-gray-400 transition-colors md:flex"
          @click="goBack"
        >
          <ArrowLeftIcon class="mr-1.5 h-4 w-4" />
          <span>{{ $t('tickets.projects.back_to_projects') }}</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <div
        class="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"
      ></div>
    </div>

    <!-- Events List - New Design -->
    <div v-else class="overflow-hidden bg-white shadow sm:rounded-md">
      <!-- Empty State -->
      <div v-if="events.length === 0" class="px-4 py-12 text-center">
        <p class="text-gray-500">{{ $t('tickets.empty_state') }}</p>
      </div>

      <!-- Events List -->
      <ul v-else role="list" class="divide-y divide-gray-200">
        <li v-for="event in events" :key="event.id">
          <div
            class="block hover:bg-gray-50"
            :class="{
              'cursor-pointer': saleOpen(event) && isNotSoldOut(event),
            }"
            @click="handleSelectEvent(event)"
          >
            <div class="flex items-center px-4 py-4 sm:px-6">
              <div
                class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between"
              >
                <div class="truncate">
                  <!-- Event Title -->
                  <div class="text-sm sm:flex sm:gap-1">
                    <p class="text-primary-600 truncate font-medium">
                      {{ event.name }}
                    </p>
                    <p
                      v-if="event.location"
                      class="shrink-0 font-normal text-gray-500 sm:ml-1"
                    >
                      in {{ event.location }}
                    </p>
                  </div>

                  <!-- Event Date, Time & Availability -->
                  <div class="mt-2 flex flex-wrap items-center gap-4">
                    <!-- Date -->
                    <div class="flex items-center text-sm text-gray-500">
                      <CalendarIcon
                        class="mr-1.5 h-5 w-5 shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <span>{{ formatDate(event.date, 'DD-MM-YYYY') }}</span>
                    </div>

                    <!-- Time -->
                    <div class="flex items-center text-sm text-gray-500">
                      <ClockIcon
                        class="mr-1.5 h-5 w-5 shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <span>{{ event.startTime }} - {{ event.endTime }}</span>
                    </div>

                    <!-- Availability Banner -->
                    <TicketsAvailableBanner :event />
                  </div>
                </div>
              </div>

              <!-- Chevron Icon -->
              <div
                v-if="isNotSoldOut(event) && saleOpen(event)"
                class="ml-5 shrink-0"
              >
                <i class="fa-regular fa-chevron-right text-gray-400"></i>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
